import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircleOutline as SuccessIcon,
  Clear as ClearIcon,
  CloudDownload as DownloadIcon,
  HelpOutline as UnknownSourceIcon,
  Language as GlobeIcon,
  Link as LinkIcon,
  MusicNote as MusicNoteIcon,
  OpenInNew as OpenInNewIcon,
  PlaylistAddCheck as BatchImportIcon,
  Search as SearchIcon,
  Star as StarIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  FormatListBulleted as ListIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { SongModel } from "../Entities/SongModel";
import { useChordLists } from "../utils/useChordLists";
import {
  apiBatchScrapeUrls,
  apiScrapeUrl,
  apiSearchExternalSongs,
  ExternalSearchResult,
} from "../utils/useDb";

const SUPPORTED_DOMAINS = [
  {
    name: "Tab4U",
    domain: "tab4u.com",
    example: "https://www.tab4u.com/tabs/songs/...",
    color: "#2563eb",
  },
  {
    name: "Ultimate Guitar",
    domain: "ultimate-guitar.com",
    example: "https://tabs.ultimate-guitar.com/tab/...",
    color: "#d97706",
  },
];

export default function ImportPage() {
  const [tabIndex, setTabIndex] = useState<number>(0);

  // Search tab states
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ExternalSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Direct URL tab states
  const [url, setUrl] = useState<string>("");

  // Import states
  const [importingUrls, setImportingUrls] = useState<Record<string, boolean>>({});
  const [importedSongs, setImportedSongs] = useState<Record<string, SongModel>>({});
  const [isBatchImporting, setIsBatchImporting] = useState<boolean>(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successSong, setSuccessSong] = useState<SongModel | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // JSON Lists tab state
  const { importLists, exportLists } = useChordLists();
  const [jsonText, setJsonText] = useState<string>("");
  const [isImportingJson, setIsImportingJson] = useState<boolean>(false);
  const [jsonImportResult, setJsonImportResult] = useState<{
    lists_imported: number;
    items_imported: number;
    warnings: string[];
  } | null>(null);
  const [jsonImportError, setJsonImportError] = useState<string | null>(null);

  const handleImportJsonSubmit = async () => {
    const raw = jsonText.trim();
    if (!raw) return;
    setIsImportingJson(true);
    setJsonImportError(null);
    setJsonImportResult(null);
    try {
      const parsed = JSON.parse(raw);
      const res = await importLists(parsed);
      setJsonImportResult(res);
      setToastMessage(`Import completed! ${res.items_imported} item(s) saved.`);
    } catch (err: any) {
      setJsonImportError(err.message || "Failed to parse and import JSON.");
    } finally {
      setIsImportingJson(false);
    }
  };

  const handleJsonFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setJsonText(text);
      setJsonImportError(null);
      setJsonImportResult(null);
    } catch (err: any) {
      setJsonImportError("Failed to read file.");
    } finally {
      e.target.value = "";
    }
  };

  const navigate = useNavigate();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const queryUrl = params.get("url") || params.get("target");
    const querySearch = params.get("search") || params.get("q") || params.get("query");
    if (queryUrl) {
      let cleanUrl = queryUrl.trim();
      if (!/^https?:\/\//i.test(cleanUrl)) {
        cleanUrl = "https://" + cleanUrl;
      }
      navigate(`/import/${encodeURIComponent(cleanUrl)}`, { replace: true });
    } else if (querySearch) {
      setSearchQuery(querySearch);
      setTabIndex(0);
    }
  }, [navigate]);

  const isAnyImporting = Object.values(importingUrls).some(Boolean);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (isAnyImporting || isBatchImporting) return;
    setTabIndex(newValue);
    setError(null);
  };

  const getDetectedSource = (targetUrl: string) => {
    const trimmed = targetUrl.trim().toLowerCase();
    if (!trimmed) return null;
    if (trimmed.includes("tab4u.com")) {
      return { name: "Tab4U", color: "#2563eb", isKnown: true };
    }
    if (trimmed.includes("ultimate-guitar.com")) {
      return { name: "Ultimate Guitar", color: "#d97706", isKnown: true };
    }
    return { name: "Unknown source", color: "#64748b", isKnown: false };
  };

  const detectedSource = getDetectedSource(url);

  const handleSearch = async (queryOverride?: string) => {
    const term = (queryOverride !== undefined ? queryOverride : searchQuery).trim();
    if (!term) return;

    if (queryOverride !== undefined) {
      setSearchQuery(queryOverride);
    }

    setIsSearching(true);
    setSearchError(null);
    setError(null);
    setHasSearched(true);

    try {
      const results = await apiSearchExternalSongs(term);
      setSearchResults(results);
    } catch (err: any) {
      console.error("External search failed:", err);
      setSearchError(
        err.message || "Failed to search online chord sources. Please try again."
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSearching && searchQuery.trim()) {
      handleSearch();
    }
  };

  const handleImportSong = async (targetUrl: string) => {
    let cleanUrl = targetUrl.trim();
    if (!cleanUrl || importingUrls[cleanUrl] || isBatchImporting) return;

    if (!/^https?:\/\//i.test(cleanUrl)) {
      cleanUrl = "https://" + cleanUrl;
    }

    setImportingUrls((prev) => ({ ...prev, [cleanUrl]: true }));
    setError(null);

    try {
      const scrapedSong = await apiScrapeUrl(cleanUrl);
      setSuccessSong(scrapedSong);
      setImportedSongs((prev) => ({
        ...prev,
        [cleanUrl]: scrapedSong,
        [targetUrl.trim()]: scrapedSong,
      }));
      setToastMessage(`Imported "${scrapedSong.title}" successfully!`);
    } catch (err: any) {
      console.error("Import failed:", err);
      setError(
        err.message || "Failed to scrape chord page. Please try another link or version."
      );
    } finally {
      setImportingUrls((prev) => ({ ...prev, [cleanUrl]: false }));
    }
  };

  const handleImportAll = async () => {
    if (searchResults.length === 0 || isBatchImporting || isAnyImporting) return;

    setIsBatchImporting(true);
    setError(null);
    setBatchProgress({ current: 0, total: searchResults.length });

    const urls = searchResults.map((r) => r.url);
    try {
      const res = await apiBatchScrapeUrls(urls);
      setToastMessage(`Imported ${res.total_imported} of ${res.total_requested} songs!`);
      if (res.imported.length > 0) {
        setImportedSongs((prev) => {
          const updated = { ...prev };
          res.imported.forEach((song) => {
            const key = song.source_url || song.url;
            if (key) {
              updated[key] = song;
            }
          });
          return updated;
        });
        setSuccessSong(res.imported[res.imported.length - 1]);
      }
    } catch (err: any) {
      console.error("Batch import failed:", err);
      setError(err.message || "Batch import failed");
    } finally {
      setIsBatchImporting(false);
      setBatchProgress(null);
    }
  };

  const handleDirectScrape = () => {
    let cleanUrl = url.trim();
    if (!cleanUrl) return;
    if (!/^https?:\/\//i.test(cleanUrl)) {
      cleanUrl = "https://" + cleanUrl;
    }
    navigate(`/import/${encodeURIComponent(cleanUrl)}`);
  };

  const isTab4U = (source: string) => source.toLowerCase().includes("tab4u");

  const renderSupportedSourcesInline = () => (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Supported Sources:
      </Typography>
      {SUPPORTED_DOMAINS.map((provider) => (
        <Chip
          key={provider.name}
          icon={<GlobeIcon fontSize="small" />}
          label={provider.name}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            borderColor: "#cbd5e1",
            bgcolor: "#ffffff",
            "& .MuiChip-icon": { color: provider.color },
          }}
        />
      ))}
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 3, sm: 6 } }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <IconButton onClick={() => navigate("/Explore")} sx={{ p: 1 }}>
            <ArrowBack />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                background: "linear-gradient(135deg, #2563eb, #4f46e5)",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
              }}
            >
              <DownloadIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Import Chords & Tabs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Search Tab4U and Ultimate Guitar or paste a direct chord URL
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: "#e2e8f0",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
            overflow: "hidden",
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#f8fafc", px: 2, pt: 1 }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab
                icon={<SearchIcon fontSize="small" />}
                iconPosition="start"
                label="Search Online Chords"
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.92rem", py: 1.5 }}
              />
              <Tab
                icon={<LinkIcon fontSize="small" />}
                iconPosition="start"
                label="Direct URL"
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.92rem", py: 1.5 }}
              />
              <Tab
                icon={<ListIcon fontSize="small" />}
                iconPosition="start"
                label="JSON Lists"
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.92rem", py: 1.5 }}
              />
            </Tabs>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 3.5 } }}>
            {/* TAB 0: Search Online Chords */}
            {tabIndex === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Search songs or artists on <strong>Tab4U</strong> (Hebrew chords) and{" "}
                  <strong>Ultimate Guitar</strong> (English/International tabs).
                </Typography>

                <Box display="flex" gap={1.5} mb={2}>
                  <TextField
                    fullWidth
                    size="medium"
                    placeholder="Search song title or artist (e.g., 'Hotel California' or 'שלמה ארצי')..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    disabled={isSearching || isBatchImporting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery("");
                              setSearchResults([]);
                              setHasSearched(false);
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSearch()}
                    disabled={isSearching || !searchQuery.trim() || isBatchImporting}
                    startIcon={
                      isSearching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />
                    }
                    sx={{
                      px: 3,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isSearching ? "Searching..." : "Search"}
                  </Button>
                </Box>

                {/* Example Quick Searches */}
                {!hasSearched && (
                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
                      Popular quick searches:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {["Hotel California", "Wonderwall", "שלמה ארצי", "אביתר בנאי", "Hallelujah", "עידן רייכל"].map(
                        (term) => (
                          <Chip
                            key={term}
                            label={term}
                            clickable
                            size="small"
                            onClick={() => handleSearch(term)}
                            sx={{
                              bgcolor: "#f1f5f9",
                              fontWeight: 500,
                              "&:hover": { bgcolor: "#e2e8f0" },
                            }}
                          />
                        )
                      )}
                    </Box>
                  </Box>
                )}

                {/* Progress bar during batch import */}
                {isBatchImporting && batchProgress && (
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        Importing songs into library...
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {batchProgress.total} songs queued
                      </Typography>
                    </Box>
                    <LinearProgress />
                  </Box>
                )}

                {/* Search Error */}
                {searchError && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {searchError}
                  </Alert>
                )}

                {/* Search Results List */}
                {searchResults.length > 0 && (
                  <Box mt={2}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                        Found {searchResults.length} matches:
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          isBatchImporting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <BatchImportIcon fontSize="small" />
                          )
                        }
                        onClick={handleImportAll}
                        disabled={isBatchImporting || isAnyImporting}
                        sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", borderRadius: 2 }}
                      >
                        {isBatchImporting ? "Importing All..." : `Import All (${searchResults.length})`}
                      </Button>
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        maxHeight: 400,
                        overflowY: "auto",
                        borderRadius: 2,
                        borderColor: "#e2e8f0",
                        bgcolor: "#ffffff",
                      }}
                    >
                      <List disablePadding>
                        {searchResults.map((result, idx) => {
                          const isBeingImported = Boolean(importingUrls[result.url] || importingUrls[`https://${result.url}`]);
                          const importedSong = importedSongs[result.url] || importedSongs[`https://${result.url}`];
                          const tab4u = isTab4U(result.source);

                          return (
                            <React.Fragment key={result.url + idx}>
                              {idx > 0 && <Divider />}
                              <ListItem
                                sx={{
                                  py: 1.25,
                                  px: 2,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  "&:hover": { bgcolor: "#f8fafc" },
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={700}
                                      noWrap
                                      sx={{ maxWidth: { xs: 180, sm: 280 } }}
                                    >
                                      {result.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      by <strong>{result.artist}</strong>
                                    </Typography>
                                  </Box>

                                  <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                                    <Chip
                                      label={result.source}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        bgcolor: tab4u ? "#eff6ff" : "#fef3c7",
                                        color: tab4u ? "#1d4ed8" : "#92400e",
                                        borderColor: tab4u ? "#bfdbfe" : "#fde68a",
                                      }}
                                    />
                                    {result.type && (
                                      <Chip
                                        label={result.type}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: "0.68rem" }}
                                      />
                                    )}
                                    {result.version && (
                                      <Typography variant="caption" color="text.secondary">
                                        ver. {result.version}
                                      </Typography>
                                    )}
                                    {result.rating !== undefined && result.rating > 0 && (
                                      <Box display="flex" alignItems="center" gap={0.25}>
                                        <StarIcon sx={{ fontSize: 13, color: "#f59e0b" }} />
                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                          {result.rating.toFixed(1)}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>

                                <Box display="flex" alignItems="center" gap={1}>
                                  <Tooltip title="View original page in new tab">
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={result.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      sx={{ color: "#64748b" }}
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {importedSong ? (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      color="success"
                                      onClick={() => navigate(`/Song?id=${importedSong.id}`)}
                                      startIcon={<SuccessIcon fontSize="small" />}
                                      sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                        borderRadius: 2,
                                        px: 1.75,
                                      }}
                                    >
                                      Open
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleImportSong(result.url)}
                                      disabled={isBeingImported || isBatchImporting}
                                      startIcon={
                                        isBeingImported ? (
                                          <CircularProgress size={14} color="inherit" />
                                        ) : (
                                          <DownloadIcon fontSize="small" />
                                        )
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                        borderRadius: 2,
                                        px: 1.75,
                                      }}
                                    >
                                      {isBeingImported ? "Importing..." : "Import"}
                                    </Button>
                                  )}
                                </Box>
                              </ListItem>
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Paper>
                  </Box>
                )}

                {/* Empty Search Result State */}
                {hasSearched && !isSearching && searchResults.length === 0 && !searchError && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: 2,
                      borderColor: "#e2e8f0",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <MusicNoteIcon sx={{ fontSize: 42, color: "text.disabled", mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      No chords found online for "{searchQuery}"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Try refining your search terms or switch to the <strong>Direct URL</strong> tab to import directly with a link.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={() => setTabIndex(1)}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      Paste Direct URL
                    </Button>
                  </Paper>
                )}
              </Box>
            )}

            {/* TAB 1: Direct URL */}
            {tabIndex === 1 && (
              <Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Paste any song or tab link from <strong>Tab4U</strong> or{" "}
                  <strong>Ultimate Guitar</strong> to parse and add to your library.
                </Typography>

                <Box display="flex" gap={1.5} mb={2}>
                  <TextField
                    fullWidth
                    size="medium"
                    placeholder="https://www.tab4u.com/tabs/songs/... or https://tabs.ultimate-guitar.com/tab/..."
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && url.trim()) {
                        handleDirectScrape();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: url && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setUrl("")}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDirectScrape}
                    disabled={!url.trim()}
                    startIcon={<DownloadIcon />}
                    sx={{
                      px: 3,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Import Song
                  </Button>
                </Box>

                {/* Detected Source Indicator */}
                {detectedSource && (
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="caption" color="text.secondary">
                      Detected Provider:
                    </Typography>
                    <Chip
                      icon={detectedSource.isKnown ? <GlobeIcon fontSize="small" /> : <UnknownSourceIcon fontSize="small" />}
                      label={detectedSource.name}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: detectedSource.isKnown ? "#eff6ff" : "#f1f5f9",
                        color: detectedSource.color,
                        borderColor: "#cbd5e1",
                      }}
                    />
                  </Box>
                )}

                {/* Supported Providers Cards */}
                <Box mt={3}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" display="block" mb={1.5}>
                    Supported Chord Providers:
                  </Typography>
                  <Grid container spacing={2}>
                    {SUPPORTED_DOMAINS.map((provider) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={provider.name}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            borderColor: "#e2e8f0",
                            bgcolor: "#f8fafc",
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <GlobeIcon sx={{ color: provider.color, fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight={700}>
                              {provider.name}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace", display: "block", wordBreak: "break-all" }}
                          >
                            {provider.example}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            )}

            {/* TAB 2: JSON Lists Import */}
            {tabIndex === 2 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Import lists using the simplistic JSON format:
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon fontSize="small" />}
                    onClick={exportLists}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    Export Current Lists
                  </Button>
                </Box>

                <Box
                  component="pre"
                  sx={{
                    bgcolor: "#f1f5f9",
                    p: 1.5,
                    borderRadius: 2,
                    fontSize: "0.8rem",
                    color: "#334155",
                    overflowX: "auto",
                    mb: 1.5,
                  }}
                >
{`{
  "To Learn": [
    {
      "url": "https://www.tab4u.com/tabs/songs/123_Song.html",
      "notes": "Practice intro chords"
    }
  ]
}`}
                </Box>

                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  Import is <strong>append-only</strong> to existing lists matched by name or ID. If a list does not exist, it will be skipped with a warning.
                </Alert>

                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={12}
                  placeholder="Paste JSON structure here..."
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  sx={{ mb: 2, fontFamily: "monospace", fontSize: "0.85rem" }}
                />

                <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleImportJsonSubmit}
                    disabled={isImportingJson || !jsonText.trim()}
                    startIcon={isImportingJson ? <CircularProgress size={18} color="inherit" /> : <BatchImportIcon />}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3 }}
                  >
                    {isImportingJson ? "Importing..." : "Import Lists"}
                  </Button>

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<FileUploadIcon fontSize="small" />}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    Upload JSON File
                    <input type="file" hidden accept=".json,application/json" onChange={handleJsonFileUpload} />
                  </Button>

                  {jsonText && (
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => {
                        setJsonText("");
                        setJsonImportResult(null);
                        setJsonImportError(null);
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>

                {jsonImportError && (
                  <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>
                    {jsonImportError}
                  </Alert>
                )}

                {jsonImportResult && (
                  <Box sx={{ mt: 2.5 }}>
                    <Alert severity="success" sx={{ borderRadius: 2, mb: 1.5 }}>
                      Successfully processed {jsonImportResult.lists_imported} list(s) and saved {jsonImportResult.items_imported} item(s).
                    </Alert>

                    {jsonImportResult.warnings && jsonImportResult.warnings.length > 0 && (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold">Warnings:</Typography>
                        <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
                          {jsonImportResult.warnings.map((w, idx) => (
                            <li key={idx}>
                              <Typography variant="body2">{w}</Typography>
                            </li>
                          ))}
                        </ul>
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Success Preview Card */}
            {successSong && (
              <Card
                variant="outlined"
                sx={{
                  mt: 2.5,
                  borderRadius: 2,
                  bgcolor: "#f0fdf4",
                  borderColor: "#86efac",
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <SuccessIcon color="success" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="#166534">
                        Successfully Imported: {successSong.title}
                      </Typography>
                      {successSong.artist && (
                        <Typography variant="body2" color="#15803d">
                          Artist: {successSong.artist}
                        </Typography>
                      )}
                    </Box>
                    {successSong.id && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => navigate(`/Song?id=${successSong.id}`)}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                      >
                        View Song
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </Container>

      <Snackbar
        open={toastMessage !== null}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
